// frontend/src/lib/surveyLogic.ts
// Survey Logic Evaluation Engine

/**
 * NOTE:
 * The survey logic engine operates on heterogeneous answer payloads
 * (string, arrays, numbers, HeatMapAnswer, PickGroupRankAnswer, etc).
 * Strong typing is intentionally relaxed here to avoid unsafe casts
 * and excessive runtime narrowing.
 */

import { 
  QuestionType, 
  LogicCondition, 
  SkipLogicRule, 
  TerminationType,
  OptionLogic,
  OptionItem,
  isHeatMapAnswer,
  isPickGroupRankAnswer,
  OTHER_OPTION_ID,
  isOtherAnswer,
} from "@/types/survey";

// ============================================
// Survey State Types
// ============================================

export interface SurveyFlowState {
  currentQuestionIndex: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- answers can be string, string[], number, HeatMapAnswer, PickGroupRankAnswer, etc.
  answers: Record<string, any>;
  visitedQuestions: string[];
  terminated: boolean;
  terminationType?: TerminationType;
  terminationMessage?: string;
  terminatedAtQuestionId?: string;
  segment?: string;
}

export interface NextQuestionResult {
  nextIndex: number;
  terminated: boolean;
  terminationType?: TerminationType;
  terminationMessage?: string;
  segment?: string;
}

// ============================================
// Condition Evaluation
// ============================================

/**
 * Safely coerce a value to number for numeric comparisons.
 * Returns null if value cannot be reasonably interpreted as a number.
 * 
 * Used only for numeric operators (greater_than, less_than, etc.)
 * to handle string answers like "25" from text inputs.
 */
const coerceNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Evaluates a single logic condition against current answers
 * 
 * @param condition - The logic condition to evaluate
 * @param answers - Current answers keyed by questionId
 * @param questionsById - Optional map for question lookup (required for row_equals/row_not_equals)
 */
export function evaluateCondition(
  condition: LogicCondition,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
  questionsById?: Map<string, QuestionType>
): boolean {
  const answer = answers[condition.questionId];
  const targetValue = condition.value;
  
  // Operators that intentionally test for missing/empty answers
  const allowMissing = ["is_empty", "is_not_empty"].includes(condition.operator);
  
  // Missing answer = condition not matched (except for emptiness operators)
  // This prevents false positives like `undefined !== "Male"` returning true
  if ((answer === undefined || answer === null) && !allowMissing) {
    return false;
  }

  switch (condition.operator) {
    case 'equals':
      // Handle "Other" option: __other__ should match any __other__:* answer
      if (targetValue === OTHER_OPTION_ID) {
        return isOtherAnswer(answer);
      }
      if (Array.isArray(answer)) {
        return answer.length === 1 && answer[0] === targetValue;
      }
      return answer === targetValue;
      
    case 'not_equals':
      // Handle "Other" option
      if (targetValue === OTHER_OPTION_ID) {
        return !isOtherAnswer(answer);
      }
      if (Array.isArray(answer)) {
        return !(answer.length === 1 && answer[0] === targetValue);
      }
      return answer !== targetValue;
      
    case 'contains':
      // Handle "Other" option: check if answer contains any __other__:* value
      if (targetValue === OTHER_OPTION_ID) {
        return isOtherAnswer(answer);
      }
      if (Array.isArray(answer)) {
        return answer.includes(targetValue);
      }
      if (typeof answer === 'string' && typeof targetValue === 'string') {
        return answer.toLowerCase().includes(targetValue.toLowerCase());
      }
      return false;
      
    case 'not_contains':
      // Handle "Other" option
      if (targetValue === OTHER_OPTION_ID) {
        return !isOtherAnswer(answer);
      }
      if (Array.isArray(answer)) {
        return !answer.includes(targetValue);
      }
      if (typeof answer === 'string' && typeof targetValue === 'string') {
        return !answer.toLowerCase().includes(targetValue.toLowerCase());
      }
      return true;
      
    case 'greater_than': {
      const numAnswer = coerceNumber(answer);
      const numTarget = coerceNumber(targetValue);
      return numAnswer !== null && numTarget !== null && numAnswer > numTarget;
    }
      
    case 'less_than': {
      const numAnswer = coerceNumber(answer);
      const numTarget = coerceNumber(targetValue);
      return numAnswer !== null && numTarget !== null && numAnswer < numTarget;
    }
      
    case 'greater_than_or_equals': {
      const numAnswer = coerceNumber(answer);
      const numTarget = coerceNumber(targetValue);
      return numAnswer !== null && numTarget !== null && numAnswer >= numTarget;
    }
      
    case 'less_than_or_equals': {
      const numAnswer = coerceNumber(answer);
      const numTarget = coerceNumber(targetValue);
      return numAnswer !== null && numTarget !== null && numAnswer <= numTarget;
    }
      
    case 'in':
      if (Array.isArray(targetValue)) {
        // Check if "__other__" is in the target values
        const hasOtherTarget = targetValue.includes(OTHER_OPTION_ID);
        const otherTargets = targetValue.filter(v => v !== OTHER_OPTION_ID);
        
        if (Array.isArray(answer)) {
          // Multi-select: check if any answer matches
          const matchesExplicit = answer.some(a => otherTargets.includes(a));
          const matchesOther = hasOtherTarget && isOtherAnswer(answer);
          return matchesExplicit || matchesOther;
        }
        
        // Single-select
        const matchesExplicit = otherTargets.includes(answer);
        const matchesOther = hasOtherTarget && isOtherAnswer(answer);
        return matchesExplicit || matchesOther;
      }
      return false;
      
    case 'not_in':
      if (Array.isArray(targetValue)) {
        const hasOtherTarget = targetValue.includes(OTHER_OPTION_ID);
        const otherTargets = targetValue.filter(v => v !== OTHER_OPTION_ID);
        
        if (Array.isArray(answer)) {
          const matchesExplicit = answer.some(a => otherTargets.includes(a));
          const matchesOther = hasOtherTarget && isOtherAnswer(answer);
          return !(matchesExplicit || matchesOther);
        }
        
        const matchesExplicit = otherTargets.includes(answer);
        const matchesOther = hasOtherTarget && isOtherAnswer(answer);
        return !(matchesExplicit || matchesOther);
      }
      return true;
      
    case 'is_empty':
      return answer === undefined || 
             answer === null || 
             answer === '' ||
             (Array.isArray(answer) && answer.length === 0);
             
    case 'is_not_empty':
      return answer !== undefined && 
             answer !== null && 
             answer !== '' &&
             !(Array.isArray(answer) && answer.length === 0);
             
    case 'any':
      return answer !== undefined && 
             answer !== null && 
             answer !== '' &&
             !(Array.isArray(answer) && answer.length === 0);
             
    case 'must_select':
      // Handle "Other" option
      if (targetValue === OTHER_OPTION_ID) {
        return isOtherAnswer(answer);
      }
      if (Array.isArray(answer)) {
        return answer.includes(targetValue);
      }
      return answer === targetValue;
      
    // =============================================
    // HEAT MAP OPERATORS
    // =============================================
    case 'clicked_region':
      if (!isHeatMapAnswer(answer)) return false;
      return answer.clicks.some(click => click.regionId === condition.regionId);
      
    case 'click_count_equals':
      if (!isHeatMapAnswer(answer)) return false;
      return answer.clicks.length === Number(targetValue);
      
    case 'click_count_gte':
      if (!isHeatMapAnswer(answer)) return false;
      return answer.clicks.length >= Number(targetValue);
      
    case 'click_count_lte':
      if (!isHeatMapAnswer(answer)) return false;
      return answer.clicks.length <= Number(targetValue);
      
    // =============================================
    // PICK GROUP RANK OPERATORS
    // =============================================
    case 'item_in_group':
      if (!isPickGroupRankAnswer(answer) || !condition.groupId || !condition.itemId) return false;
      return answer.groups[condition.groupId]?.includes(condition.itemId) ?? false;
      
    case 'group_contains':
      if (!isPickGroupRankAnswer(answer) || !condition.groupId) return false;
      return answer.groups[condition.groupId]?.includes(String(targetValue)) ?? false;
      
    case 'group_count_equals':
      if (!isPickGroupRankAnswer(answer) || !condition.groupId) return false;
      return (answer.groups[condition.groupId]?.length ?? 0) === Number(targetValue);
      
    case 'group_count_gte':
      if (!isPickGroupRankAnswer(answer) || !condition.groupId) return false;
      return (answer.groups[condition.groupId]?.length ?? 0) >= Number(targetValue);
      
    case 'all_items_sorted':
      if (!isPickGroupRankAnswer(answer)) return false;
      return answer.unsorted.length === 0;

    // =============================================
    // ROW EQUALS / ROW NOT EQUALS (Grid Questions)
    // These operators check if a specific row has a specific column selected
    // Only supported for radio_grid and checkbox_grid question types
    // =============================================
    case 'row_equals':
    case 'row_not_equals': {
      // Guard 1: questionsById must be provided
      if (!questionsById) return false;

      // Guard 2: Question must exist
      const question = questionsById.get(condition.questionId);
      if (!question) return false;

      // Guard 3: Must be radio_grid or checkbox_grid
      if (!['radio_grid', 'checkbox_grid'].includes(question.type)) return false;

      // Guard 4: rows and columns must exist and be non-empty
      if (!question.rows?.length || !question.columns?.length) return false;

      // Guard 5: value must be GridConditionValue with rowId and columnId
      const gridValue = targetValue as { rowId?: string; columnId?: string };
      if (!gridValue?.rowId || !gridValue?.columnId) return false;

      // Find row and column indices from IDs
      const rowIndex = question.rows.findIndex(r => r.id === gridValue.rowId);
      const colIndex = question.columns.findIndex(c => c.id === gridValue.columnId);
      
      // Guard 6: IDs must resolve to valid indices
      if (rowIndex === -1 || colIndex === -1) return false;

      // Get respondent's answer for this grid question
      // Format: { rowIndex: columnIndex } for radio_grid
      //         { rowIndex: number[] } for checkbox_grid
      const gridAnswer = answer as Record<number | string, number | number[]> | undefined;
      
      if (!gridAnswer) {
        // No answer: row_equals = false, row_not_equals = true
        return condition.operator === 'row_not_equals';
      }

      // Get the answer for this specific row (try both number and string keys)
      const rowAnswer = gridAnswer[rowIndex] ?? gridAnswer[String(rowIndex)];

      // If row wasn't answered: row_equals = false, row_not_equals = true
      if (rowAnswer === undefined) {
        return condition.operator === 'row_not_equals';
      }

      // Determine if the column was selected for this row
      let columnSelected = false;
      if (Array.isArray(rowAnswer)) {
        // checkbox_grid: array of selected column indices
        columnSelected = rowAnswer.includes(colIndex);
      } else if (typeof rowAnswer === 'number') {
        // radio_grid: single selected column index
        columnSelected = rowAnswer === colIndex;
      }

      return condition.operator === 'row_equals' ? columnSelected : !columnSelected;
    }
      
    default:
      console.warn(`Unknown logic operator: ${condition.operator}`);
      return false;
  }
}

/**
 * Evaluates all conditions in a rule set
 * 
 * @param conditions - Array of conditions to evaluate
 * @param operator - "AND" (all must pass) or "OR" (any must pass)
 * @param answers - Current answers keyed by questionId
 * @param questionsById - Optional map for question lookup (required for row_equals/row_not_equals)
 */
export function evaluateConditions(
  conditions: LogicCondition[],
  operator: 'AND' | 'OR',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
  questionsById?: Map<string, QuestionType>
): boolean {
  if (conditions.length === 0) return true;
  
  const results = conditions.map(c => evaluateCondition(c, answers, questionsById));
  
  return operator === 'AND' 
    ? results.every(Boolean)
    : results.some(Boolean);
}

// ============================================
// Skip Logic Evaluation
// ============================================

/**
 * Evaluates skip logic rules and returns the first matching rule
 * 
 * @param rules - Array of skip logic rules to evaluate
 * @param answers - Current answers keyed by questionId
 * @param questionsById - Optional map for question lookup (required for row_equals/row_not_equals)
 */
export function evaluateSkipLogic(
  rules: SkipLogicRule[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
  questionsById?: Map<string, QuestionType>
): SkipLogicRule | null {
  for (const rule of rules) {
    const match = evaluateConditions(
      rule.conditions, 
      rule.conditionOperator, 
      answers,
      questionsById
    );
    
    if (match) return rule;
  }
  return null;
}

// ============================================
// Display Condition Evaluation
// ============================================

/**
 * Determines if a question should be displayed based on its conditions
 * 
 * @param question - The question to check
 * @param answers - Current answers keyed by questionId
 * @param questionsById - Optional map for question lookup (required for row_equals/row_not_equals)
 */
export function shouldShowQuestion(
  question: QuestionType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
  questionsById?: Map<string, QuestionType>
): boolean {
  // No conditions = always show
  if (!question.displayConditions || question.displayConditions.length === 0) {
    return true;
  }
  
  for (const dc of question.displayConditions) {
    const match = evaluateConditions(
      dc.conditions, 
      dc.conditionOperator, 
      answers,
      questionsById
    );
    
    if (match) return dc.show;
    
    // Per spec: "If show=true and conditions NOT MET → SKIP, try next"
    // When a "show if" condition doesn't match, hide the question
    if (dc.show === true) return false;
  }
  
  // Default: show if only "hide if" conditions existed and none matched
  return true;
}

// ============================================
// Option Randomization
// ============================================

/**
 * Randomizes options while respecting [ANCHOR] and [EXCLUSIVE] markers.
 * 
 * Algorithm:
 * 1. Separate into: regular (shuffle), anchored (fixed position), exclusive (end)
 * 2. Fisher-Yates shuffle regular options
 * 3. Re-insert anchored at original positions
 * 4. Append exclusive at the end
 * 
 * See "Option Randomization Algorithm" section for full specification.
 */
export function randomizeOptionsWithAnchors(
  options: OptionItem[],
  optionLogic?: OptionLogic[]
): OptionItem[] {
  if (!options || options.length <= 1) return options;
  
  // STEP 1: Separate into three buckets - using option ID for logic lookup
  const regular: OptionItem[] = [];
  const anchored: { option: OptionItem; index: number }[] = [];
  const exclusive: OptionItem[] = [];
  
  options.forEach((option, index) => {
    // Look up logic by option ID (stable) rather than optionValue (text)
    const logic = optionLogic?.find(ol => ol.optionId === option.id);
    
    if (logic?.isExclusive) {
      // Exclusive options always go to end (even if also anchored)
      exclusive.push(option);
    } else if (logic?.isAnchor) {
      // Anchored options remember their original position
      anchored.push({ option, index });
    } else {
      // Regular options will be shuffled
      regular.push(option);
    }
  });
  
  // STEP 2: Fisher-Yates shuffle regular options
  const shuffled = [...regular];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // STEP 3: Re-insert anchored options at original positions
  const withAnchors: OptionItem[] = [...shuffled];
  anchored
    .sort((a, b) => a.index - b.index)
    .forEach(({ option, index }) => {
      const insertAt = Math.min(index, withAnchors.length);
      withAnchors.splice(insertAt, 0, option);
    });
  
  // STEP 4: Append exclusive options at the end
  return [...withAnchors, ...exclusive];
}

// ============================================
// Question Randomization
// ============================================

/**
 * Randomizes question order while respecting anchored questions.
 * 
 * Use cases:
 * - Brand studies (randomize brand questions to avoid order bias)
 * - Attribute ratings (randomize attribute presentation order)
 * - Concept testing (randomize which concept is shown first)
 * 
 * Algorithm:
 * 1. Separate questions into anchored (fixed) and regular (shuffle)
 * 2. Fisher-Yates shuffle regular questions
 * 3. Re-insert anchored questions at their original positions
 * 
 * @param questions - Array of questions to randomize
 * @param anchoredIds - Question IDs that should stay in their original positions
 * @returns Randomized question array
 */
export function randomizeQuestionOrder(
  questions: QuestionType[],
  anchoredIds?: string[]
): QuestionType[] {
  if (!questions || questions.length <= 1) return questions;
  
  const anchoredSet = new Set(anchoredIds || []);
  
  // STEP 1: Separate anchored and non-anchored questions
  const regular: QuestionType[] = [];
  const anchored: { question: QuestionType; index: number }[] = [];
  
  questions.forEach((question, index) => {
    if (anchoredSet.has(question.id)) {
      anchored.push({ question, index });
    } else {
      regular.push(question);
    }
  });
  
  // STEP 2: Fisher-Yates shuffle non-anchored questions
  const shuffled = [...regular];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // STEP 3: Re-insert anchored questions at original positions
  const result: QuestionType[] = [...shuffled];
  anchored
    .sort((a, b) => a.index - b.index)
    .forEach(({ question, index }) => {
      const insertAt = Math.min(index, result.length);
      result.splice(insertAt, 0, question);
    });
  
  return result;
}

// ============================================
// Option Logic Lookup
// ============================================

/**
 * Looks up option logic using priority-based matching.
 * 
 * Now that answers store option IDs (not text), primary lookup is by optionId.
 * Supports both single selection (string) and multi-selection (string[]).
 * For multi-select, returns the first option with skip logic defined.
 * 
 * Special handling for "Other" answers:
 * - "__other__:<text>" answers match option logic with optionId === "__other__"
 * 
 * Priority: optionId (stable ID) > optionCode > optionValue (legacy text match)
 */
export function findOptionLogic(
  answerId: string | string[],
  optionLogic?: OptionLogic[]
): OptionLogic | undefined {
  if (!optionLogic) return undefined;
  
  // Helper to check if an answer matches an option logic entry
  const matchesOptionLogic = (id: string, ol: OptionLogic): boolean => {
    // Direct ID match
    if (ol.optionId === id) return true;
    
    // "Other" answer matching: "__other__:<text>" matches optionId === "__other__"
    if (ol.optionId === OTHER_OPTION_ID && 
        (id === OTHER_OPTION_ID || id.startsWith(`${OTHER_OPTION_ID}:`))) {
      return true;
    }
    
    return false;
  };
  
  // Handle multi-select: find first option with skip logic
  if (Array.isArray(answerId)) {
    for (const id of answerId) {
      const logic = optionLogic.find(ol => matchesOptionLogic(id, ol));
      if (logic?.skipLogic) return logic;
    }
    return undefined;
  }
  
  // Single selection: check for "Other" match first, then priority-based lookup
  const otherMatch = optionLogic.find(ol => matchesOptionLogic(answerId, ol));
  if (otherMatch) return otherMatch;
  
  // Fallback to legacy lookups (optionCode, optionValue)
  return optionLogic.find(ol => ol.optionCode === answerId) ||
         optionLogic.find(ol => ol.optionValue === answerId);
}

// ============================================
// Navigation Logic
// ============================================

/**
 * Determines the next question based on STRICT evaluation order:
 * 
 * STEP 1: optionLogic (most specific - per-option routing)
 * STEP 2: skipLogic (question-level routing rules)  
 * STEP 3: Check segmentation rules (optional)
 * STEP 4: Find next candidate filtered by displayConditions
 * 
 * See "Logic Evaluation Model" section for full specification.
 */
export function getNextQuestion(
  questions: QuestionType[],
  currentIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers: Record<string, any>,
  selectedOption?: string | string[]
): NextQuestionResult {
  const currentQuestion = questions[currentIndex];
  
  // Build questionsById map once for efficient lookups (required for row_equals/row_not_equals)
  const questionsById = new Map(questions.map(q => [q.id, q]));
  
  // Normalize selected options to array
  const selectedOptions = Array.isArray(selectedOption) 
    ? selectedOption 
    : selectedOption ? [selectedOption] : [];
  
  // ═══════════════════════════════════════════════════════════════
  // STEP 1: Check optionLogic (most specific - per-option routing)
  // Uses "Most Restrictive Wins" priority for multi-select conflicts
  // Priority: terminate > skip_to_end > skip_to_question > continue
  // ═══════════════════════════════════════════════════════════════
  if (currentQuestion.optionLogic && selectedOptions.length > 0) {
    // Collect all matching option logic with routing
    const matchedLogic = selectedOptions
      .map(opt => findOptionLogic(opt, currentQuestion.optionLogic))
      .filter((ol): ol is OptionLogic => ol?.skipLogic !== null && ol?.skipLogic !== undefined);
    
    if (matchedLogic.length > 0) {
      // Priority 1: terminate (highest)
      const terminateLogic = matchedLogic.find(ol => ol.skipLogic?.action === 'terminate');
      if (terminateLogic && terminateLogic.skipLogic) {
        const rule = terminateLogic.skipLogic;
        return { 
          nextIndex: -1, 
          terminated: true, 
          terminationType: rule.terminationType || 'screen_out',
          terminationMessage: rule.terminationMessage
        };
      }
      
      // Priority 2: skip_to_end
      const skipToEndLogic = matchedLogic.find(ol => ol.skipLogic?.action === 'skip_to_end');
      if (skipToEndLogic) {
        return { 
          nextIndex: -1, 
          terminated: true, 
          terminationType: 'completed' 
        };
      }
      
      // Priority 3: skip_to_question
      const skipToQuestionLogic = matchedLogic.find(ol => ol.skipLogic?.action === 'skip_to_question');
      if (skipToQuestionLogic && skipToQuestionLogic.skipLogic) {
        const rule = skipToQuestionLogic.skipLogic;
        if (!rule.targetQuestionId) {
          console.error(`[Logic Engine] Option skip rule missing targetQuestionId`);
          // Fall through to Step 2
        } else {
          const targetIndex = questions.findIndex(q => q.id === rule.targetQuestionId);
          if (targetIndex === -1) {
            console.error(`[Logic Engine] Option skip target not found: ${rule.targetQuestionId}`);
          } else if (targetIndex <= currentIndex) {
            console.error(`[Logic Engine] Option skip target not forward: ${targetIndex}`);
          } else {
            return { 
              nextIndex: targetIndex, 
              terminated: false
            };
          }
        }
      }
      // Priority 4: continue - fall through to Step 2
    }
  }
  
  // ═══════════════════════════════════════════════════════════════
  // STEP 2: Check skipLogic (question-level routing rules)
  // First matching rule wins
  // ═══════════════════════════════════════════════════════════════
  if (currentQuestion.skipLogic && currentQuestion.skipLogic.length > 0) {
    const matchedRule = evaluateSkipLogic(currentQuestion.skipLogic, answers, questionsById);
    
    if (matchedRule) {
      if (matchedRule.action === 'terminate') {
        return { 
          nextIndex: -1, 
          terminated: true, 
          terminationType: matchedRule.terminationType || 'screen_out',
          terminationMessage: matchedRule.terminationMessage
        };
      }
      
      if (matchedRule.action === 'skip_to_question') {
        // DEFENSIVE: Validate targetQuestionId
        if (!matchedRule.targetQuestionId) {
          console.error(`[Logic Engine] Skip rule missing targetQuestionId`);
          // Fall through to next question
        } else {
          const targetIndex = questions.findIndex(q => q.id === matchedRule.targetQuestionId);
          if (targetIndex === -1) {
            console.error(`[Logic Engine] Skip target not found: ${matchedRule.targetQuestionId}`);
          } else if (targetIndex <= currentIndex) {
            console.error(`[Logic Engine] Skip target not forward: ${targetIndex} <= ${currentIndex}`);
          } else {
            return { nextIndex: targetIndex, terminated: false };
          }
        }
      }
      
      if (matchedRule.action === 'skip_to_end') {
        return { nextIndex: -1, terminated: true, terminationType: 'completed' };
      }
    }
  }
  
  // ═══════════════════════════════════════════════════════════════
  // STEP 3: Check segmentation rules (optional - assigns user to segment)
  // ═══════════════════════════════════════════════════════════════
  let segment: string | undefined;
  if (currentQuestion.segmentationRules) {
    for (const rule of currentQuestion.segmentationRules) {
      const match = evaluateConditions(
        rule.conditions,
        rule.conditionOperator || 'AND',  // Respect user's choice, default to AND
        answers,
        questionsById
      );
      if (match) {
        segment = rule.segmentName;
        break;
      }
    }
  }
  
  // ═══════════════════════════════════════════════════════════════
  // STEP 4: Find next visible question (filtered by displayConditions)
  // ═══════════════════════════════════════════════════════════════
  for (let i = currentIndex + 1; i < questions.length; i++) {
    if (shouldShowQuestion(questions[i], answers, questionsById)) {
      return { nextIndex: i, terminated: false, segment };
    }
  }
  
  // No more visible questions - survey completed
  return { 
    nextIndex: -1, 
    terminated: true, 
    terminationType: 'completed',
    segment 
  };
}

// ============================================
// Survey State Management
// ============================================

/**
 * Creates initial survey flow state
 */
export function createInitialFlowState(): SurveyFlowState {
  return {
    currentQuestionIndex: 0,
    answers: {},
    visitedQuestions: [],
    terminated: false,
  };
}

/**
 * Checks if survey has any logic configured
 */
export function surveyHasLogic(questions: QuestionType[]): boolean {
  return questions.some(q => 
    (q.skipLogic && q.skipLogic.length > 0) ||
    (q.displayConditions && q.displayConditions.length > 0) ||
    (q.optionLogic && q.optionLogic.length > 0) ||
    (q.segmentationRules && q.segmentationRules.length > 0)
  );
}

/**
 * Gets count of questions with each type of logic
 */
export function getLogicStats(questions: QuestionType[]): {
  withSkipLogic: number;
  withDisplayConditions: number;
  withOptionLogic: number;
  withSegmentation: number;
  terminationPoints: number;
} {
  let terminationPoints = 0;
  
  questions.forEach(q => {
    // Count terminations in skip logic
    q.skipLogic?.forEach(rule => {
      if (rule.action === 'terminate') terminationPoints++;
    });
    
    // Count terminations in option logic
    q.optionLogic?.forEach(ol => {
      if (ol.skipLogic?.action === 'terminate') terminationPoints++;
    });
  });
  
  return {
    withSkipLogic: questions.filter(q => q.skipLogic && q.skipLogic.length > 0).length,
    withDisplayConditions: questions.filter(q => q.displayConditions && q.displayConditions.length > 0).length,
    withOptionLogic: questions.filter(q => q.optionLogic && q.optionLogic.length > 0).length,
    withSegmentation: questions.filter(q => q.segmentationRules && q.segmentationRules.length > 0).length,
    terminationPoints,
  };
}

/**
 * Validates that all logic references are valid.
 * 
 * IMPORTANT: Skip targets must be FORWARD only (target index > current index).
 * This constraint is essential because:
 * 1. Frontend engine assumes forward-only navigation
 * 2. Backward jumps can create infinite loops
 * 3. Supporting arbitrary jumps requires a full graph engine with cycle detection
 */
export function validateLogicReferences(questions: QuestionType[]): string[] {
  const errors: string[] = [];
  const questionIds = new Set(questions.map(q => q.id));
  
  // Build position map for forward-only validation
  const questionPositions = new Map(questions.map((q, idx) => [q.id, idx]));
  
  questions.forEach((q, currentIndex) => {
    const qLabel = q.originalQuestionNumber || `Question ${currentIndex + 1}`;
    
    // Validate skip logic targets
    q.skipLogic?.forEach((rule, ruleIndex) => {
      if (rule.action === 'skip_to_question') {
        // DEFENSIVE: Check targetQuestionId is present
        if (!rule.targetQuestionId) {
          errors.push(
            `${qLabel}, Rule ${ruleIndex + 1}: Action is 'skip_to_question' but targetQuestionId is missing`
          );
        } else if (!questionIds.has(rule.targetQuestionId)) {
          // Check target exists
          errors.push(`${qLabel}, Rule ${ruleIndex + 1}: Target question ID not found`);
        } else {
          // Check target is FORWARD (not backward or same question)
          const targetIndex = questionPositions.get(rule.targetQuestionId) ?? -1;
          if (targetIndex <= currentIndex) {
            errors.push(
              `${qLabel}, Rule ${ruleIndex + 1}: Skip target must be forward. ` +
              `Cannot skip to question at position ${targetIndex + 1} from position ${currentIndex + 1}. ` +
              `Backward jumps are not supported (would require cycle detection).`
            );
          }
        }
      }
      
      // Validate condition references
      rule.conditions.forEach((cond, condIndex) => {
        if (cond.questionId && !questionIds.has(cond.questionId)) {
          errors.push(`${qLabel}, Rule ${ruleIndex + 1}, Condition ${condIndex + 1}: Referenced question not found`);
        }
      });
    });
    
    // Validate option logic targets
    q.optionLogic?.forEach((ol) => {
      if (ol.skipLogic?.action === 'skip_to_question') {
        // DEFENSIVE: Check targetQuestionId is present
        if (!ol.skipLogic.targetQuestionId) {
          errors.push(
            `${qLabel}, Option "${ol.optionValue}": Action is 'skip_to_question' but targetQuestionId is missing`
          );
        } else if (!questionIds.has(ol.skipLogic.targetQuestionId)) {
          // Check target exists
          errors.push(`${qLabel}, Option "${ol.optionValue}": Target question not found`);
        } else {
          // Check target is FORWARD
          const targetIndex = questionPositions.get(ol.skipLogic.targetQuestionId) ?? -1;
          if (targetIndex <= currentIndex) {
            errors.push(
              `${qLabel}, Option "${ol.optionValue}": Skip target must be forward. ` +
              `Cannot skip to question at position ${targetIndex + 1} from position ${currentIndex + 1}.`
            );
          }
        }
      }
    });
    
    // Validate display condition references
    // Note: Display conditions reference PREVIOUS questions (must be backward)
    q.displayConditions?.forEach((dc, dcIndex) => {
      dc.conditions.forEach((cond) => {
        if (cond.questionId) {
          if (!questionIds.has(cond.questionId)) {
            errors.push(`${qLabel}, Display Condition ${dcIndex + 1}: Referenced question not found`);
          } else {
            // Display conditions should reference EARLIER questions
            const refIndex = questionPositions.get(cond.questionId) ?? -1;
            if (refIndex >= currentIndex) {
              errors.push(
                `${qLabel}, Display Condition ${dcIndex + 1}: ` +
                `Condition references question at position ${refIndex + 1}, ` +
                `but display conditions can only reference earlier questions.`
              );
            }
          }
        }
      });
    });
  });
  
  return errors;
}
